import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  Stack,
  InputLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Icon,
} from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import Loader from '../../common/Loader';

// import CustomersFormattedMessages from '../../../messages/admin/CustomersFormattedMessages';

// NETWORKING
import useComplianceRequest from '../../../requests/ComplianceRequest';
import { getZoneOffsettedDate } from '../../../utils/dates';

const Trial = ({ errorHandler, navigate }) => {
  const [trial, setTrial] = useState(null);
  const [trialSearch, setTrialSearch] = useState({});

  const { getCampaignManagementTrial } = useComplianceRequest();

  useEffect(() => {
    const fetchTrial = async () => {
      const trialData = await getCampaignManagementTrial(errorHandler);
      console.log('trial data:', trialData);
      setTrial(trialData);
    };
    fetchTrial();
  }, []);

  /*
    Handles changes in the form
  */
  const handleTrialSearchChange = async (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setTrialSearch(value);
  };

  const handleSearch = async (updatedSearch) => {
    try {
      setTrial(null);
      const trialData = await getCampaignManagementTrial(
        errorHandler,
        updatedSearch
      );
      console.log('trialData:', trialData);
      setTrial(trialData);
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleKeyDown = async (event) => {
    const { key } = event;
    if (key === 'Enter') handleSearch(trialSearch);
  };

  const generatePendingBrandsTable = () => {
    let tableContent = (
      <TableRow key={null}>
        <TableCell>
          <Loader />
        </TableCell>
      </TableRow>
    );

    if (trial?.pendingBrand) {
      tableContent = trial.pendingBrand.map((brand) => {
        return (
          <TableRow key={brand.brand_code}>
            <TableCell sx={{ width: 70 }}>
              <Link
                onClick={() =>
                  navigate('/admin/compliance/brand/' + brand.brand_id)
                }
                tabIndex={0}
              >
                {brand.brand_code}
              </Link>
            </TableCell>
            <TableCell>
              {brand.customer_id ? (
                <Link
                  onClick={() => {
                    navigate('/admin/customers/' + brand.customer_id);
                  }}
                  tabIndex={0}
                >
                  {brand.customer_name}
                </Link>
              ) : (
                'N/A'
              )}
            </TableCell>
            <TableCell sx={{ width: 240 }}>
              {brand.brand_relationship}
            </TableCell>
            <TableCell sx={{ width: 240 }}>{brand.vertical}</TableCell>
            <TableCell sx={{ width: 240 }}>{brand.entity_type}</TableCell>
          </TableRow>
        );
      });
    } else if (trial) {
      tableContent = (
        <TableRow key={null}>
          <TableCell>No pending brands</TableCell>
        </TableRow>
      );
    }

    return (
      <Box>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell sx={{ width: 70 }}></TableCell>
                <TableCell>{'Pending Brands'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Relationship'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Vertical'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Entity Type'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{tableContent}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const generatePendingCampaignsTable = () => {
    let tableContent = (
      <TableRow key={null}>
        <TableCell>
          <Loader />
        </TableCell>
      </TableRow>
    );

    if (trial?.pendingCampaign) {
      tableContent = trial.pendingCampaign.map((campaign, i) => {
        const campaignNotAdded = campaign.campaign_code === 'UNKNOWN';
        const warningButton = (
          <Icon color="error" sx={{ display: 'flex' }}>
            <ErrorOutline />
          </Icon>
        );

        return (
          <TableRow key={i}>
            <TableCell sx={{ width: 70 }}>
              <Link
                onClick={() => {
                  navigate(
                    `/admin/compliance/campaign/${campaign.campaign_id}`
                  );
                }}
                tabIndex={0}
              >
                {campaign.campaign_code}
              </Link>
            </TableCell>
            <TableCell>
              {campaign.customer_id ? (
                <Link
                  onClick={() => {
                    navigate('/admin/customers/' + campaign.customer_id);
                  }}
                  tabIndex={0}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    {campaign.customer_name}
                    {campaignNotAdded && warningButton}
                  </Stack>
                </Link>
              ) : (
                'N/A'
              )}
            </TableCell>
            <TableCell sx={{ width: 240 }}>{campaign.use_case}</TableCell>
            <TableCell sx={{ width: 240 }}>{campaign.vertical}</TableCell>
            <TableCell sx={{ width: 240 }}>{campaign.brand_code}</TableCell>
          </TableRow>
        );
      });
    } else if (trial) {
      tableContent = (
        <TableRow key={null}>
          <TableCell>No pending campaigns</TableCell>
        </TableRow>
      );
    }

    return (
      <Box>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell sx={{ width: 70 }}></TableCell>
                <TableCell>{'Pending Campaigns'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Use Case'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Vertical'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Brand'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{tableContent}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const generateExistingTrialsTable = () => {
    let tableContent = (
      <TableRow key={null}>
        <TableCell>
          <Loader />
        </TableCell>
      </TableRow>
    );

    if (trial?.otherTrialCustomers) {
      tableContent = trial.otherTrialCustomers.map((customer) => {
        return (
          <TableRow key={customer.customer_id}>
            <TableCell>
              <Link
                onClick={() => {
                  navigate('/admin/customers/' + customer.customer_id);
                }}
                tabIndex={0}
              >
                {customer.customer_name}
              </Link>
            </TableCell>
            <TableCell sx={{ width: 240 }}>{customer.country_name}</TableCell>
            <TableCell sx={{ width: 240 }}>
              {getZoneOffsettedDate(customer.created_date)}
            </TableCell>
            <TableCell sx={{ width: 240 }}>
              {getZoneOffsettedDate(customer.expiry_date)}
            </TableCell>
          </TableRow>
        );
      });
    } else if (trial) {
      tableContent = (
        <TableRow key={null}>
          <TableCell>No existing trials</TableCell>
        </TableRow>
      );
    }

    return (
      <Box>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell>{'Existing Trials'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Country'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Started'}</TableCell>
                <TableCell sx={{ width: 240 }}>{'Expiry'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{tableContent}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          backgroundColor: 'common.white',
          borderRadius: '0 0 20px 20px',
          paddingX: 4,
          paddingY: 2,
          maxWidth: 800,
        }}
      >
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            justifyContent: 'space-between',
            alignItems: { md: 'center' },
            gap: 2,
          }}
        >
          <Stack sx={{ flex: 1 }}>
            <InputLabel>
              {'Search by customer name, brand code, or campaign code'}
            </InputLabel>

            <Stack sx={{ flex: 1, flexDirection: 'row', gap: 2 }}>
              <TextField
                id="contactSearch"
                autoFocus
                type="text"
                name="searchContent"
                onKeyDown={handleKeyDown}
                onChange={handleTrialSearchChange}
                variant="outlined"
                fullWidth
                size="small"
              />
              <Button
                variant="outlined"
                color="secondary"
                name="submit"
                onClick={() => handleSearch(trialSearch)}
              >
                Search
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      {generatePendingBrandsTable()}
      {generatePendingCampaignsTable()}
      {generateExistingTrialsTable()}
    </React.Fragment>
  );
};

export default Trial;
